import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Button } from '@mui/material';
import { PageLayout } from '../../PageLayout';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { ComponentName, getMetaData, Link } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { addDrawer, getFromDrawerSubject } from '../../../store/Drawer';
import {
  Asset,
  AssetUpdate,
  fetchAsset,
  getAssetSubject,
  handleCommissionsInfoValidation,
  handleRpmAccountValidation,
  handleValidationStatusValidation,
  updateAsset
} from '../../../store/Asset';
import { RPMMiniTable } from '../../Compounds/RPM/RPMMiniTable';
import {
  assetSectionsInfo,
  commissionsSectionInfo,
  assetValidationInfo,
  assetRpmAccountsInfo,
  assetPayoutInfo
} from './assetHelpers';
import { isData, isObserver } from '../../../store/User';
import { DetailsLinkCTA } from '../../Compounds/DetailsLinkCTA';
import { NewLinkDialog } from '../../Compounds/Linking/NewLinkDialog/NewLinkDialog';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByAssetId } from '../../../store/Ticket';
import {
  fetchRpmAccountsByAsset,
  getCruxRpmAccountsByAssetLoadingSubject,
  getCruxRpmAccountsByAssetSubject
} from '../../../store/RpmAccount';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { deleteLink } from '../../../store/Link';
import { getFormattedDate } from '../../../utils/helpers';
import { DetailViewValidationCard, ValidatorResult } from '../../Compounds/CardWithTitle';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { formatValidationMessages, handleRunValidator } from '../../Compounds/CardWithTitle/validationCardHelpers';
import { DecommissionParentDialog } from './DecommissionParentDialog';
import { AssetPayments } from './AssetPayments';
import { getSelectedTab, updateState } from '../../../store/GeneralStore';
import { TabPanel } from '../../Atoms/TabPanel';
import { AllocationMiniTable } from '../../Compounds/Allocation/AllocationMiniTable';
import { AttributionTable } from '../../Compounds/Attribution/AttributionTable';
import { CorrectionMiniTable } from '../../Compounds/Correction/CorrectionTable';
import { AdjustmentMiniTable } from '../../Compounds/Adjustment/AdjustmentTable';
import { SpiffTable } from '../../Compounds/Spiff/SpiffTable';
import {
  fetchCorrections,
  getTallyCorrectionsLoadingSubject,
  getTallyCorrectionsSubject
} from '../../../store/Correction';
import {
  fetchAdjustments,
  getTallyAdjustmentsLoadingSubject,
  getTallyAdjustmentsSubject
} from '../../../store/Adjustment';
import { HistoryMiniTable } from '../../Compounds/History/HistoryTable';
import {
  fetchChangeHistory,
  getChangeHistoryLoadingSubject,
  getChangeHistorySubject
} from '../../../store/ChangeHistory';

const tabs = ['Info', 'Reconcile', 'Allocation', 'History'];

export const AssetDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState<Asset>({} as Asset);
  const [linkGroup, setLinkGroup] = useState<Link[]>([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [link, setLink] = useState('');
  const [tabIndex, setTabIndex] = useState<number>(
    getSelectedTab().assetDetail ? Number(getSelectedTab().assetDetail) : 0
  );
  const [isLinkFormOpen, setIsLinkFormOpen] = useState<boolean>(false);
  const [validationStatusBySection, setValidationStatusBySection] = useState<Record<string, ValidatorResult>>({});
  const navigate = useNavigate();
  const { setSnack } = useSnackbar();

  const cardActions = () => {
    return isObserver() || isData() ? <></> : <DetailsLinkCTA setIsFormOpen={() => setIsLinkFormOpen(true)} />;
  };

  const handleTabChange = (tabIndex: number) => {
    setTabIndex(tabIndex);
    updateState({
      selectedTab: {
        ...getSelectedTab(),
        assetDetail: tabIndex
      }
    });
  };

  const handleLoadData = async () => {
    if (id) {
      setAssetLoading(true);
      const errorStatusCode = await fetchAsset(id);
      if (errorStatusCode) navigate(`/error/${errorStatusCode}/asset/${id}/${new Date().getTime()}`);
      setAssetLoading(false);
    }
  };

  const handleBack = () => {
    if (getFromDrawerSubject().getValue()) {
      addDrawer({
        id: v4(),
        data,
        ...getMetaData({ name: ComponentName.ASSET, sfId: data.sf_id || '', id: data.id, data })
      });
    }
    window.history.length > 1 ? navigate(-1) : navigate('/asset');
  };

  useEffect(() => {
    const assetSub = getAssetSubject().subscribe((a) => {
      setData(a);
      const assetMeta = getMetaData({ name: ComponentName.ASSET, sfId: a.sf_id || '', id: a.id, data: a });
      setLink(assetMeta.link || '');
      const linkGroupData = assetMeta.linkGroup;
      if (linkGroupData) {
        setLinkGroup(linkGroupData);
      }
    });

    return () => {
      if (assetSub) assetSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    handleLoadData();
  }, [id]);

  useEffect(() => {
    const linkGroupData = getMetaData({ name: ComponentName.ASSET, id: id || '', sfId: data.sf_id, data }).linkGroup;
    if (linkGroupData) {
      setLinkGroup(linkGroupData);
    }
  }, [id]);

  useEffect(() => {
    const messages = formatValidationMessages(validationStatusBySection);
    if (messages.length) {
      setSnack({
        message: messages.join(', '),
        type: 'warning',
        duration: 15000,
        open: true
      });
    }
  }, [JSON.stringify(validationStatusBySection)]);

  const pageActions = () => {
    return (
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<ArrowBackIosIcon />}
        onClick={handleBack}>
        Back
      </Button>
    );
  };

  const tabContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <div className="grid grid-cols-2 space-x-6">
            <div className="space-y-6">
              <DetailViewValidationCard
                enableCollapse
                key="commissions_detail"
                dataTestId="commission-detail-container"
                data={commissionsSectionInfo(data)}
                saveAction={(d) => saveAction(d as AssetUpdate)}
                detailValidator={(d) =>
                  handleRunValidator(
                    'commissionsInfo',
                    d,
                    handleCommissionsInfoValidation,
                    validationStatusBySection,
                    setValidationStatusBySection
                  )
                }
                title="Commissions"
                wrapperClassName="min-h-[15rem]"
              />
              <DetailViewValidationCard
                enableCollapse
                key="asset_table"
                dataTestId="rpm-account-detail-container"
                data={assetRpmAccountsInfo(data)}
                cardActions={cardActions()}
                detailValidator={(d) =>
                  handleRunValidator(
                    'rpmAccounts',
                    d,
                    handleRpmAccountValidation,
                    validationStatusBySection,
                    setValidationStatusBySection
                  )
                }
                title="RPM Accounts">
                <RPMMiniTable
                  id={data.id}
                  fetchFunction={fetchRpmAccountsByAsset}
                  removeFunction={deleteLink}
                  dataSubject={getCruxRpmAccountsByAssetSubject}
                  loadingSubject={getCruxRpmAccountsByAssetLoadingSubject}
                  showRemove
                />
              </DetailViewValidationCard>
            </div>
            <div className="space-y-6">
              <DetailViewCard
                enableCollapse
                key="asset-detail-card"
                title="Info"
                dataTestId="info-detail-container">
                <div className="min-h-[15rem]">
                  <DetailPanelContainer
                    data={assetSectionsInfo(data)}
                    saveAction={(d) => saveAction(d as AssetUpdate)}
                  />
                </div>
              </DetailViewCard>
            </div>
          </div>
        );
      case 1:
        return (
          <Fragment>
            <AttributionTable
              entityType="asset"
              entityId={id as string}
            />
            <AdjustmentMiniTable
              id={id as string}
              fetchFunction={fetchAdjustments}
              dataSubject={getTallyAdjustmentsSubject}
              loadingSubject={getTallyAdjustmentsLoadingSubject}
            />
            <SpiffTable
              entityType="asset"
              entityId={id as string}
            />
            <CorrectionMiniTable
              id={id as string}
              fetchFunction={fetchCorrections}
              dataSubject={getTallyCorrectionsSubject}
              loadingSubject={getTallyCorrectionsLoadingSubject}
              assetNumber={data.number || ''}
              supplierName={data.supplierName || ''}
              commissionsStatus={data.commissionsStatus || ''}
              type="asset"
            />
          </Fragment>
        );
      case 2:
        return (
          <AllocationMiniTable
            id={id as string}
            type="combined-assets"
          />
        );
      case 3:
        return (
          <HistoryMiniTable
            id={id as string}
            fetchFunction={fetchChangeHistory}
            dataSubject={getChangeHistorySubject}
            loadingSubject={getChangeHistoryLoadingSubject}
            recordType="asset"
          />
        );
      default:
        return null;
    }
  };

  const saveAction = (update: AssetUpdate) => updateAsset(update);
  const withValidationSection = tabIndex === 0 || tabIndex === 2;

  return (
    <PageLayout
      className="mb-10"
      dataTestId="asset-detail-page"
      subTitle="Asset"
      pageTitle={data.name}
      link={link}
      showLinkGroup={true}
      linkGroup={linkGroup}
      showIcon={true}
      icon="assetMed"
      key={data.id}
      showCopy={true}
      copyValue={data.number || ''}
      pageActions={pageActions()}
      tabs={tabs}
      handleTabChange={handleTabChange}
      activeTab={tabIndex}>
      {assetLoading && (
        <ComponentSpinner
          sx={{ m: 'auto', height: '100%' }}
          label="Loading Data..."
        />
      )}
      {!assetLoading && data && (
        <div className="grid grid-cols-3 mt-6 space-x-6">
          {tabs.map((tab, index) => (
            <TabPanel
              key={tab}
              value={tabIndex}
              index={index}
              boxPaddingTop={0}
              className={`${withValidationSection ? 'col-span-2' : 'col-span-3'}`}>
              {tabContent()}
            </TabPanel>
          ))}
          {withValidationSection && (
            <div className="space-y-6">
              <DetailViewValidationCard
                enableCollapse
                key="asset-validation-card"
                title="Validation"
                dataTestId="validation-detail-container"
                data={assetValidationInfo(data)}
                saveAction={(d) => saveAction(d as AssetUpdate)}
                detailValidator={(d) =>
                  handleRunValidator(
                    'commissionsValidation',
                    d,
                    handleValidationStatusValidation,
                    validationStatusBySection,
                    setValidationStatusBySection
                  )
                }
              />
              <DetailViewCard
                enableCollapse
                key="asset-payout-card"
                title="Payout"
                dataTestId="payout-detail-container">
                <div className="min-h-[5rem]">
                  <DetailPanelContainer data={assetPayoutInfo(data)} />
                  <AssetPayments asset={data} />
                </div>
              </DetailViewCard>
              <TicketMiniTable
                id={data.id}
                fetchFunction={fetchTicketsByAssetId}
                association={{
                  type: 'asset',
                  body: {
                    entityId: id
                  }
                }}
                showCreate={true}
                objectType="asset"
                objectData={data}
                objectLinkId={data.id}
                objectLinkDisplay={`Asset ${data.number}`}
              />
              <DetailsComments
                id={data.id}
                entityType="asset"
              />
              <DetailViewCard
                enableCollapse
                key="meta_detail"
                title="Meta"
                dataTestId="meta-detail-container">
                <div className="flex px-4 pt-4 w-full">
                  <LabelValueField
                    label="Salesforce ID"
                    value={data.sf_id}
                    field="sf_id"
                  />
                  <LabelValueField
                    label="UPSTACK ID"
                    value={data.id}
                    field="id"
                  />
                </div>
                <div className="flex px-4 w-full">
                  <LabelValueField
                    label="Created At"
                    value={getFormattedDate(data.createdAt, 'lll')}
                    field="createdAt"
                  />
                  <LabelValueField
                    label="Modified At"
                    value={getFormattedDate(data.modifiedDate, 'lll')}
                    field="modifiedDate"
                  />
                </div>
              </DetailViewCard>
              <NewLinkDialog
                open={isLinkFormOpen}
                setOpen={setIsLinkFormOpen}
                asset={data}
              />
              <DecommissionParentDialog asset={data} />
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};
